import Button from '@mui/material/Button';
import { useTranslation } from '@tyro/i18n';
import { CopyIcon } from '@tyro/icons';
import { type ComponentProps, useEffect } from 'react';
import { useCopyToClipboard } from 'react-use';
import { toast } from 'sonner';

type CopyClipboardButtonProps = ComponentProps<typeof Button> & {
  textToCopy: string;
  buttonLabel?: string;
  successMessage?: string;
  errorMessage?: string;
  showIconOnHover?: boolean;
};

export function CopyClipboardButton({
  textToCopy,
  buttonLabel,
  successMessage,
  errorMessage,
  sx,
  showIconOnHover = true,
  ...buttonProps
}: CopyClipboardButtonProps) {
  const { t } = useTranslation(['common']);
  const [state, copyToClipboard] = useCopyToClipboard();

  useEffect(() => {
    if (state.error) {
      toast.error(errorMessage ?? t('common:issueCopyingToClipboard'));
    } else if (state.value) {
      toast.success(successMessage ?? t('common:copiedToClipboard'));
    }
  }, [state, successMessage, errorMessage]);

  return (
    <Button
      size="small"
      sx={{
        fontSize: '0.75rem',
        justifyContent: 'flex-start',
        minWidth: 'auto',
        px: 1,
        ...(showIconOnHover && {
          '& .MuiButton-endIcon': {
            opacity: 0,
            transition: 'opacity 0.2s ease-in-out',
          },
          '&:hover .MuiButton-endIcon': {
            opacity: 1,
          },
        }),
        ...sx,
      }}
      onClick={() => copyToClipboard(textToCopy)}
      endIcon={<CopyIcon fontSize="inherit" />}
      {...buttonProps}
    >
      {buttonLabel ?? textToCopy}
    </Button>
  );
}
